import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDevServerUrl, setIFrameChanged } from "@/store/iframeSlice";
import "mousetrap/plugins/global-bind/mousetrap-global-bind";
import { toast } from "sonner";
import axios from "axios";
import {
  getAxiosErorrMessage,
  getDevServerUrl,
  getUnexpiredToken,
  isDemoUser,
  logAxiosError,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
  useUserId,
} from "@/utils/utils";
import {
  fetchDevServerStatus,
  setGithubAccess,
  setGitlabAccess,
} from "@/store/codebaseSlice";
import { track } from "@/utils/events/tracker";

import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import {
  useLaunchContainer,
  useLoadCodebase,
  useStartServer,
} from "@/utils/devServerUtils";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { fetchGitStatus, GitProvider } from "@/store/gitStatusSlice";
import { ProjectRunMode, ReactFramework } from "@/utils/templateProjects";
import {
  DevtoolStatus,
  extractDevtool,
  useCodebaseStore,
} from "@/store/zustand/codebaseStore";
import useLocalMode from "./useLocalMode";
import { useLocation } from "react-router";
import { Canvas } from "@/utils/types";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { useTypedSelector } from "@/store/store";
import { useUser } from "@clerk/clerk-react";
import { useSession } from "@clerk/clerk-react";

export const useProjectLaunch = () => {
  const { user } = useUser();
  const demoUser = isDemoUser(user);

  const { session } = useSession();

  const location = useLocation();

  const canvasId = useCanvasId();

  const { isLocalMode, localPort } = useLocalMode();

  const dispatch = useDispatch<any>();

  const mainCanvas = useActiveMainCanvas();

  // Codebase store
  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const latestDevtoolVersion = useCodebaseStore(
    (state) => state.latestDevtoolVersion
  );
  const conditions = useCodebaseStore((state) => state.conditions);

  const dependencies = useCodebaseStore((state) => state.dependencies);
  const setLatestDevtoolVersion = useCodebaseStore(
    (state) => state.setLatestDevtoolVersion
  );
  const setProjectLaunchStatus = useCodebaseStore(
    (state) => state.setProjectLaunchStatus
  );
  const setShowDevtoolModal = useCodebaseStore(
    (state) => state.setShowDevtoolModal
  );
  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse
  );

  const [installingNewDevTools, setInstallingNewDevTools] = useState(false);
  const setAllLookups = useCodebaseStore((state) => state.setAllLookups);

  const {
    supabaseClient,
    triggerRefetchActivityStream,
    triggerRefetchProjectLoadingProgress,
  } = useSupabase();

  const devServerStatus = useTypedSelector(
    (state) => state.codebase.devServerStatus
  );
  const devServerRequestStatus = useSelector(
    (state: any) => state.codebase.devServerRequestStatus
  );

  const startServer = useStartServer();
  const launchContainer = useLaunchContainer();

  // Project from DB
  const project = useActiveProject();

  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress
  );

  // For Local Mode - track if the dev server has started up and if it disconnects
  const [localModeDevServerStatus, setLocalModeDevServerStatus] =
    useState("loading");

  const prevCanvasId = usePrevious(canvasId);
  const canvasIdRef = useRef(canvasId);
  useEffect(() => {
    canvasIdRef.current = canvasId;
  }, [canvasId]);

  const runningProjectLoadRef = useRef(false);

  const loadCodebase = useLoadCodebase();

  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const unauthorized = useSelector((state: any) => state.codebase.unauthorized);
  const deleted = useSelector((state: any) => state.codebase.deleted);

  const [triggerFetchDevServerStatus, setTriggerFetchDevServerStatus] =
    useState(false);

  useEffect(() => {
    if (devServerRequestStatus === "loading") {
      return;
    }

    setTriggerFetchDevServerStatus(true);
  }, [projectLaunchStatus]);

  /**
   * Fetch the dev server status which is needed to know how to launch a project
   */
  useEffect(() => {
    if (!session || isLocalMode || !canvasId) {
      return;
    }

    setTriggerFetchDevServerStatus(false);

    dispatch(
      fetchDevServerStatus({
        canvasId,
        supabaseClient,
        session,
      })
    );

    // Fetch once every 5 seconds, and once every minute for expo (because it takes longer)
    // Also, only do this if the project launch status is succeeded
    let intervalId;
    if (projectLaunchStatus === "succeeded") {
      intervalId = setInterval(() => {
        setTriggerFetchDevServerStatus(true);
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    canvasId,
    Boolean(session),
    isLocalMode,
    triggerFetchDevServerStatus,
    projectLaunchStatus,
  ]);

  const [fetchedGitStatus, setFetchedGitStatus] = useState(false);

  const userId = useUserId();

  /**
   * Check if user has access to Github project.
   */
  useEffect(() => {
    // Already fetched
    if (fetchedGitStatus) {
      return;
    }

    if (!session || !canvasId || unauthorized === true || deleted) {
      return;
    }

    if (!project?.repoPath) {
      return;
    }

    // Data not loaded yet
    if (!projectLoadingProgress || !mainCanvas || !user) {
      return;
    }

    // Only check Github access if the user owns the canvas, or if the project needs to be cloned
    if (
      mainCanvas.owner_user_id !== userId &&
      projectLoadingProgress.cloning_done
    ) {
      return;
    }

    setFetchedGitStatus(true);

    const fetchGitlabAccess = async () => {
      // automatically return true if the project is not a gitlab project - we shouldn't check for gitlab access
      if (project.provider !== "gitlab") {
        dispatch(setGitlabAccess(true));
        return;
      }

      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/gitProvider/repos`,
          {
            repoPath: project.repoPath,
            gitProvider: project.provider,
            gitlab_project_id: project.gitlab_project_id,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGitlabAccess(true));
        } else {
          dispatch(setGitlabAccess(false));
        }
      } catch (error) {
        console.error("Error fetching gitlab access", error);
        if (error.response && error.response.status === 404) {
          dispatch(setGitlabAccess(false));
        }
      }
    };

    const fetchGithubAccess = async () => {
      const provider = project.provider;

      // automatically return true if the project is not a gitlab project - we shouldn't check for gitlab access
      // we also default to github if there is no provider set so check that there also is a provider
      if (provider && provider !== GitProvider.GITHUB) {
        dispatch(setGithubAccess(true));
        return;
      }

      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/gitProvider/repos`,
          {
            repoPath: project.repoPath,
            gitProvider: provider,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGithubAccess(true));
        } else {
          dispatch(setGithubAccess(false));
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          dispatch(setGithubAccess(false));
        }
      }
    };

    fetchGithubAccess();
    fetchGitlabAccess();
  }, [
    Boolean(session),
    unauthorized,
    deleted,
    canvasId,
    project,
    user,
    projectLoadingProgress,
  ]);

  /**
   * File watcher
   */
  const [socketUrl, setSocketUrl] = useState(null);
  const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    reconnectAttempts: 0,
    heartbeat: {
      timeout: 30000,
    },
  });

  const [fetchingSocketUrl, setFetchingSocketUrl] = useState(false);

  useEffect(() => {
    // Reset so it tries to open again
    if (
      readyState === ReadyState.CLOSED ||
      readyState === ReadyState.UNINSTANTIATED
    ) {
      setSocketUrl(null);
    }
  }, [readyState]);

  /**
   * Load the websocket token & url for the file watcher, but only after the project has been cloned
   */
  useEffect(() => {
    if (!session || !canvasId || !projectLoadingProgress?.files_loaded) {
      return;
    }

    if (fetchingSocketUrl || socketUrl) {
      return;
    }

    if (
      !(
        readyState === ReadyState.CLOSED ||
        readyState === ReadyState.UNINSTANTIATED
      )
    ) {
      return;
    }

    setFetchingSocketUrl(true);

    getUnexpiredToken(session).then((supabaseJWT) =>
      axios
        .get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/websocketToken`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((response) => {
          const fileWatcherToken = response?.data?.token;
          setSocketUrl(
            // @ts-expect-error TS2345
            `${
              import.meta.env.VITE_BACKEND_WS_BASE_URL
            }/canvases/${canvasId}/fileWatcher?token=${fileWatcherToken}`
          );
          setFetchingSocketUrl(false);
        })
        .catch((error) => {
          logAxiosError("Error fetching websocket token", error);
          setFetchingSocketUrl(false);
        })
    );
  }, [
    canvasId,
    Boolean(session),
    readyState,
    projectLoadingProgress?.files_loaded,
    socketUrl,
  ]);

  /**
   * Listen to messages on the file watcher websocket
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    if (lastMessage !== null) {
      if (!lastMessage.data) {
        return;
      }

      const data = JSON.parse(lastMessage.data);
      if (!data) {
        return;
      }

      updateLookupsBasedOnPartialResponse(data);
      triggerRefetchActivityStream();
      dispatch(
        fetchGitStatus({
          canvasId,
          session,
        })
      );

      dispatch(setIFrameChanged());
    }
  }, [lastMessage, Boolean(session)]);

  /**
   * Check if local mode is connected or not
   */
  useEffect(() => {
    if (!isLocalMode) {
      return;
    }

    let timeoutId;
    let useEffectCleared = false;

    const checkIfDevServerRunning = () => {
      axios
        .get("http://localhost:2993")
        .then(() => {
          setLocalModeDevServerStatus("running");

          if (!useEffectCleared) {
            timeoutId = setTimeout(checkIfDevServerRunning, 3000);
          }
        })
        .catch((error) => {
          if (localModeDevServerStatus === "running") {
            setLocalModeDevServerStatus("disconnected");
          }

          if (!useEffectCleared) {
            timeoutId = setTimeout(checkIfDevServerRunning, 3000);
          }
        });
    };

    checkIfDevServerRunning();

    return () => {
      useEffectCleared = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLocalMode, localModeDevServerStatus]);

  const getLatestDevtoolVersion = async () => {
    const supabaseJWT = await getUnexpiredToken(session);
    const response = await axios.get(
      `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/latestDevtoolVersion`,
      {
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      }
    );
    return response.data;
  };

  /**
   * Load the latest devtools version before launching the project
   *
   * This is the most recent published version (not the one for the project), and is statically
   * changed when the backend is deployed
   */
  useEffect(() => {
    if (!session || !canvasId || unauthorized !== false || deleted) {
      return;
    }

    if (latestDevtoolVersion) {
      return;
    }

    getLatestDevtoolVersion()
      .then((latestDevtoolVersion) => {
        setLatestDevtoolVersion(latestDevtoolVersion);
      })
      .catch((e) => {
        console.error("error fetching latest devtool version", e);
      });
  }, [Boolean(session), unauthorized, deleted]);

  /**
   * Returns whether the force upgrade/install devtools modal should be shown
   */
  const shouldShowDevtoolsModal = (
    conditions,
    dependencies,
    expectedDevtoolVersion
  ): { showModal: boolean; installDevtools: boolean } => {
    // Fetch the latest status from the response
    const [status, _] = expectedDevtoolVersion
      ? extractDevtool(dependencies || {}, expectedDevtoolVersion)
      : [DevtoolStatus.Latest, null];

    const devtoolInitializedInfo = conditions?.devtoolInitialized;

    const babelPluginInstalled = conditions?.babelPluginInstalled;
    const swcPluginInstalled = conditions?.swcPluginInstalled;
    const anyPluginInstalled =
      babelPluginInstalled?.met || swcPluginInstalled?.met;

    const pluginInfoRecieved =
      anyPluginInstalled || (babelPluginInstalled && swcPluginInstalled);

    const gitignoreSetupInfo = conditions?.gitignoreSetup;

    // Only test each requirement if that information is returned from the backend
    // When a codebase is first cloned that info may not be checked
    const installDevtools = status < DevtoolStatus.Latest;
    const showModal =
      (devtoolInitializedInfo && !devtoolInitializedInfo.met) ||
      (pluginInfoRecieved && !anyPluginInstalled) ||
      (gitignoreSetupInfo && !gitignoreSetupInfo.met);

    return { installDevtools, showModal };
  };

  const handleInstallDevtools = async (): Promise<boolean> => {
    if (!session || !supabaseClient) {
      return false;
    }

    setInstallingNewDevTools(true);

    await supabaseClient
      .from("project_loading_progress")
      .update({
        installed_devtools: false,
        last_updated: new Date().toISOString(),
      })
      .eq("canvas_id", canvasId);

    triggerRefetchProjectLoadingProgress();

    const supabaseJWT = await getUnexpiredToken(session);

    try {
      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/writeLatestDevtoolToProject`,
        {},
        {
          headers: {
            Authorization: `Bearer ${supabaseJWT}`,
          },
        }
      );

      setAllLookups({ lookups: response.data, canvasId });
      dispatch(
        fetchGitStatus({
          canvasId,
          session,
        })
      );
      setInstallingNewDevTools(false);
      await supabaseClient
        .from("project_loading_progress")
        .update({
          installed_devtools: true,
          last_updated: new Date().toISOString(),
        })
        .eq("canvas_id", canvasId);

      triggerRefetchProjectLoadingProgress();

      return true;
    } catch (e) {
      logAxiosError("Error installing devtools", e);
      setInstallingNewDevTools(false);
      return false;
    }
  };

  /**
   * Whenever the dev tools version is loaded, check if the modal should be showed
   *
   * Note: this is meant to be run after the project launch flow has finished
   * (succeeded or failed)
   */
  useEffect(() => {
    // For all other cases the project launch flow will handle it
    if (projectLaunchStatus !== "succeeded" && projectLaunchStatus !== "idle") {
      return;
    }

    // If currently loading, don't process here
    if (runningProjectLoadRef.current) {
      return;
    }

    if (!latestDevtoolVersion || !supabaseClient) {
      return;
    }

    // Conditions not yet loaded
    if (Object.keys(conditions || {}).length === 0) {
      return;
    }

    // Dependencies not yet loaded
    if (Object.keys(dependencies?.regularDependencies || {}).length === 0) {
      return;
    }

    const { showModal: showDevtoolModal, installDevtools } =
      shouldShowDevtoolsModal(conditions, dependencies, latestDevtoolVersion);

    if (installDevtools) {
      if (installingNewDevTools) {
        return;
      }

      if (!project || project.auto_install_off) {
        setShowDevtoolModal(true);
        return;
      }

      setProjectLaunchStatus("installing");
      handleInstallDevtools().then((succeeded) => {
        // Needed to show the modal anyway
        if (showDevtoolModal) {
          setShowDevtoolModal(true);
          return;
        }

        if (succeeded) {
          startServer({
            forceRestart: true,
          });
        } else {
          setShowDevtoolModal(true);
        }
      });
    } else if (showDevtoolModal) {
      setShowDevtoolModal(true);
    } else if (
      projectLoadingProgress &&
      !projectLoadingProgress.installed_devtools
    ) {
      const markInstalled = async () => {
        await supabaseClient
          .from("project_loading_progress")
          .update({
            installed_devtools: true,
            last_updated: new Date().toISOString(),
          })
          .eq("canvas_id", canvasId);

        triggerRefetchProjectLoadingProgress();
      };
      markInstalled();
    }
  }, [latestDevtoolVersion, conditions, dependencies, supabaseClient]);

  const loadedDevServerStatus = Boolean(devServerStatus);

  /**
   * When the user is logged in, load and launch the project
   */
  useEffect(() => {
    if (!session || !supabaseClient) {
      return;
    }

    if (runningProjectLoadRef.current) {
      return;
    }

    if (!project) {
      return;
    }

    if (!loadedDevServerStatus && !isLocalMode) {
      return;
    }

    if (!project || !mainCanvas) {
      return;
    }

    // Any other statuses means it is currently launching
    if (projectLaunchStatus !== "succeeded" && projectLaunchStatus !== "idle") {
      return;
    }

    if (projectLaunchStatus === "succeeded" && canvasId === prevCanvasId) {
      return;
    }

    runningProjectLoadRef.current = true;
    setProjectLaunchStatus("cloning");

    // Container launch tests
    const needToLaunchContainer =
      (!devServerStatus?.containerStatus?.running ||
        !devServerStatus?.containerStatus?.configUpToDate) &&
      !isLocalMode &&
      project?.runMode === ProjectRunMode.CLOUD;

    // Dev server launch tests
    const needToStartDevServer =
      needToLaunchContainer ||
      ((!devServerStatus?.isServerRunning ||
        !devServerStatus?.devServerConsoleConnected) &&
        !isLocalMode &&
        project?.runMode === ProjectRunMode.CLOUD);

    const run = async () => {
      const updateDict = {
        canvas_id: canvasId,
        files_loaded: false,
        last_updated: new Date().toISOString(),
        initialized_env: true,
      };

      if (needToLaunchContainer) {
        updateDict["launched_container"] = false;
      } else {
        updateDict["launched_container"] = true;
        updateDict["cloning_done"] = true;
      }

      if (needToStartDevServer) {
        updateDict["started_dev_server"] = false;
        updateDict["node_modules_installed"] = false;
        updateDict["dev_server_responding"] = false;
      } else {
        updateDict["started_dev_server"] = true;
        updateDict["node_modules_installed"] = true;
        updateDict["dev_server_responding"] = true;
        updateDict["cloning_done"] = true;
      }

      const { error } = await supabaseClient
        .from("project_loading_progress")
        .upsert(updateDict, {
          onConflict: "canvas_id",
        });

      triggerRefetchProjectLoadingProgress();

      if (error) {
        console.error("Update progress error", error);
      }
      // TODO: make this better by first checking if the project has already been cloned & lookups loaded

      setProjectLaunchStatus("loading");
      dispatch(
        setDevServerUrl({
          canvasId,
          url: null,
        })
      );

      try {
        const loadResponse = await loadCodebase();

        if (!loadResponse) {
          setProjectLaunchStatus("error");
          return;
        }

        const freshLatestDevtoolVersion = await getLatestDevtoolVersion();

        const { showModal: showDevtoolModal, installDevtools } =
          shouldShowDevtoolsModal(
            loadResponse.data?.conditions,
            loadResponse.data?.dependencies,
            freshLatestDevtoolVersion
          );

        // Always launch the container no matter what
        if (needToLaunchContainer) {
          const { success } = await launchContainer();

          if (!success) {
            setProjectLaunchStatus("error");
            return;
          }
        }

        if (installDevtools && !project.auto_install_off) {
          const succeeded = await handleInstallDevtools();
          if (!succeeded || showDevtoolModal) {
            // Get the user to install the devtools manually through the modal if not succeeded
            console.debug("Not launching, tempo devtools not installed...");
            setShowDevtoolModal(true);
            return;
          }
        } else if (showDevtoolModal) {
          console.debug("Not launching, tempo devtools not installed...");
          setShowDevtoolModal(true);
          return;
        } else {
          setShowDevtoolModal(false);
          await supabaseClient
            .from("project_loading_progress")
            .update({
              installed_devtools: true,
              last_updated: new Date().toISOString(),
            })
            .eq("canvas_id", canvasId);

          triggerRefetchProjectLoadingProgress();
        }

        if (needToStartDevServer) {
          const { success } = await startServer({
            doNotCheckContainer: true,
            doNotReloadCodebase: true,
          });
          if (!success) {
            setProjectLaunchStatus("error");
            return;
          }
        }

        triggerRefetchProjectLoadingProgress();

        // Project successfully loaded, fetch a fresh canvas to get the latest info
        // and update the dev server url
        const { data: freshCanvas, error } = await supabaseClient
          .from("canvases")
          .select(
            `
            *,
            canvas_machine_mapping (
              machine_id,
              machine:deployment_machines (
                id,
                dev_server_suffix,
                is_new_active,
                new_dev_server_suffix,
                url
              )
            )
          `
          )
          .eq("id", canvasId)
          .single();

        if (canvasId !== canvasIdRef.current) {
          return;
        }

        setTriggerFetchDevServerStatus(true);

        const machine = freshCanvas?.canvas_machine_mapping?.machine;
        const devServerSuffix = machine?.is_new_active
          ? machine?.new_dev_server_suffix
          : machine?.dev_server_suffix;

        const devServerUrl = getDevServerUrl(
          freshCanvas as unknown as Canvas,
          devServerSuffix || "",
          isLocalMode,
          localPort
        );

        dispatch(
          setDevServerUrl({
            canvasId,
            url: devServerUrl,
          })
        );

        setProjectLaunchStatus("succeeded");
        track("PROJECT_LAUNCHED");
      } catch (error) {
        if (canvasId !== canvasIdRef.current) {
          return;
        }

        setProjectLaunchStatus("error");
        logAxiosError("Error launching project", error);
        toast.error("Error launching project: " + getAxiosErorrMessage(error));
        return;
      }
    };

    run().then(() => {
      runningProjectLoadRef.current = false;
    });
  }, [
    projectLaunchStatus,
    project,

    // Only trigger use effect when the dev server status is created, not when changed
    loadedDevServerStatus,
    Boolean(session),
    Boolean(supabaseClient),
  ]);
};
